import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import LazyImage from './LazyImage';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  const isAuthenticated = !!localStorage.getItem('token') || !!localStorage.getItem('xchange_token');

  // Warm Premium Palette
  const colors = {
    primaryTeal: '#3D6B67',
    primaryTealLight: '#4D8B86',
    primaryBrown: '#5C4033',
    primaryBrownDark: '#4A3328',
    bgCream: '#FAF6F2',
    bgSage: '#DCE7E3',
    bgCard: '#FFFFFF',
    textPrimary: '#2C1810',
    textSecondary: '#5D4E47',
    textOnDark: '#FFFFFF',
    borderWarm: '#E8E0D8',
  };

  const logoUrl = 'https://customer-assets.emergentagent.com/job_impact-consulting-1/artifacts/0x7ttskj_image.png';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Pages with dark hero backgrounds where white text is needed
  const darkHeroPages = ['/', '/about-us', '/contact', '/csr-landscape', '/governance'];
  const hasDarkHero = darkHeroPages.includes(location.pathname);
  
  // Use dark text on pages without dark hero backgrounds OR when scrolled
  const useDarkText = isScrolled || !hasDarkHero;

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('xchange_token');
    localStorage.removeItem('xchange_user');
    navigate('/');
  };

  const navLinks = [
    { path: '/csr-landscape', label: 'CSR Landscape' },
    { path: '/governance', label: 'Governance Suite' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <header
      data-testid="main-header"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{ 
        backgroundColor: isScrolled || !hasDarkHero ? 'rgba(250, 246, 242, 0.97)' : 'transparent',
        backdropFilter: isScrolled || !hasDarkHero ? 'blur(12px)' : 'none',
        borderBottom: isScrolled || !hasDarkHero ? `1px solid ${colors.borderWarm}` : 'none',
        boxShadow: isScrolled ? '0 1px 3px rgba(44, 24, 16, 0.04)' : 'none'
      }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
            <div className="overflow-hidden rounded-lg" style={{ height: '40px', width: '40px' }}>
              <LazyImage 
                src={logoUrl} 
                alt="CSR Exchange Logo" 
                className="h-full w-full"
                placeholderColor={colors.bgSage}
                style={{ height: '60px', width: 'auto', marginTop: '-2px', objectFit: 'cover', objectPosition: 'top' }}
                data-testid="header-logo"
              />
            </div>
            <span 
              className="text-xl font-bold tracking-tight transition-colors duration-200"
              style={{ color: useDarkText ? colors.textPrimary : colors.textOnDark }}
            >
              CSR Exchange
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
                style={{
                  color: location.pathname === link.path 
                    ? (useDarkText ? colors.primaryTeal : colors.textOnDark)
                    : (useDarkText ? colors.textSecondary : 'rgba(255, 255, 255, 0.85)'),
                  backgroundColor: location.pathname === link.path 
                    ? (useDarkText ? colors.bgSage : 'rgba(255, 255, 255, 0.12)')
                    : 'transparent',
                  fontWeight: location.pathname === link.path ? '600' : '500'
                }}
                onMouseEnter={(e) => {
                  if (location.pathname !== link.path) {
                    e.currentTarget.style.color = useDarkText ? colors.textPrimary : colors.textOnDark;
                    e.currentTarget.style.backgroundColor = useDarkText ? colors.bgSage : 'rgba(255, 255, 255, 0.12)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (location.pathname !== link.path) {
                    e.currentTarget.style.color = useDarkText ? colors.textSecondary : 'rgba(255, 255, 255, 0.85)';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            {isAuthenticated ? (
              <button 
                onClick={handleLogout} 
                className="text-sm font-medium transition-colors px-4 py-2 rounded-lg"
                style={{ color: useDarkText ? colors.textSecondary : 'rgba(255, 255, 255, 0.85)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = useDarkText ? colors.textPrimary : colors.textOnDark;
                  e.currentTarget.style.backgroundColor = useDarkText ? colors.bgSage : 'rgba(255, 255, 255, 0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = useDarkText ? colors.textSecondary : 'rgba(255, 255, 255, 0.85)';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Sign Out
              </button>
            ) : (
              <>
                <button
                  onClick={() => navigate('/xchange/login')}
                  data-testid="login-btn"
                  className="px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200"
                  style={{ color: useDarkText ? colors.textSecondary : 'rgba(255, 255, 255, 0.85)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = useDarkText ? colors.textPrimary : colors.textOnDark;
                    e.currentTarget.style.backgroundColor = useDarkText ? colors.bgSage : 'rgba(255, 255, 255, 0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = useDarkText ? colors.textSecondary : 'rgba(255, 255, 255, 0.85)';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Sign In
                </button>
                <button
                  onClick={() => navigate('/')}
                  data-testid="get-started-btn"
                  className="px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200"
                  style={{ 
                    backgroundColor: colors.primaryTeal, 
                    color: colors.textOnDark,
                    boxShadow: '0 2px 8px rgba(61, 107, 103, 0.3)'
                  }}
                  onMouseEnter={(e) => { 
                    e.currentTarget.style.backgroundColor = colors.primaryTealLight;
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => { 
                    e.currentTarget.style.backgroundColor = colors.primaryTeal;
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Get Started
                </button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg transition-all"
            style={{ color: useDarkText ? colors.textPrimary : colors.textOnDark }}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div 
            data-testid="mobile-menu" 
            className="lg:hidden py-6"
            style={{ borderTop: `1px solid ${colors.borderWarm}`, backgroundColor: colors.bgCream }}
          >
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-3 rounded-lg text-sm font-medium transition-colors"
                  style={{
                    color: location.pathname === link.path ? colors.primaryTeal : colors.textPrimary,
                    backgroundColor: location.pathname === link.path ? colors.bgSage : 'transparent'
                  }}
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex flex-col gap-3 pt-4 mt-2" style={{ borderTop: `1px solid ${colors.borderWarm}` }}>
                {isAuthenticated ? (
                  <button
                    onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                    className="w-full px-4 py-3 rounded-lg text-sm font-medium text-left"
                    style={{ color: colors.primaryTeal }}
                  >
                    Sign Out
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => { navigate('/xchange/login'); setIsMobileMenuOpen(false); }}
                      className="w-full px-4 py-3 rounded-lg text-sm font-medium text-left"
                      style={{ color: colors.textPrimary }}
                    >
                      Sign In
                    </button>
                    <button
                      onClick={() => { navigate('/'); setIsMobileMenuOpen(false); }}
                      className="w-full px-6 py-3.5 rounded-xl text-sm font-semibold text-center"
                      style={{ backgroundColor: colors.primaryTeal, color: colors.textOnDark }}
                    >
                      Get Started
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
